import * as THREE from 'three/webgpu';
import { pass, mix, vertexColor } from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';

import { uNight } from '../core/uniforms.js';
import { streamRng, range } from '../math/rng.js';
import { clamp } from '../math/mathx.js';

/**
 * @file T0 STACK PROOF — delete or replace once real levels exist (T7/T8/T12).
 *
 * Exercises, in one screen, every load-bearing assumption of the plan:
 *   - `three/webgpu` + `three/tsl` imports with no bare-'three' anywhere
 *   - `MeshBasicNodeMaterial` with NO lights and NO shadow maps
 *   - non-indexed, flat-shaded geometry with per-FACE vertex colours
 *   - the two-channel bake convention: `color` (day) + `color1` (night),
 *     blended in TSL by the shared `uNight` uniform
 *   - `THREE.RenderPipeline` (r183 rename) with `pass(scene, camera,
 *     {samples:4})` for MSAA and a mild bloom
 *   - the `Level` contract from `engine/types.js`, including `dispose()`
 *   - seeded RNG only — no `Math.random()`
 */

/** Baked sun direction (surface -> sun): ~42° elevation, camera-side azimuth.
 *  A LEVEL CONSTANT, exactly like a real level's. */
const SUN = new THREE.Vector3(0.42, 0.67, 0.61).normalize();

const DAY_SKY = new THREE.Color(0.55, 0.66, 0.86);
const DAY_GROUND = new THREE.Color(0.34, 0.3, 0.26);
const NIGHT_HEMI = new THREE.Color(0.14, 0.18, 0.32);

/**
 * Builds the test level (an icosahedron that spins and breathes day->night).
 * @type {import('../types.js').LevelBuilder}
 */
export function buildTestLevel(ctx) {
  const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
  const { registry, onProgress } = ctx;
  const rng = streamRng(ctx.seed ?? 'test', 'testScene');

  onProgress?.(0.1, 'geometry');

  // detail 0 => 20 faces. PolyhedronGeometry is already non-indexed, but the
  // project rule is "assert it" — merged/baked geometry must never be indexed.
  let geo = new THREE.IcosahedronGeometry(2.4, 0);
  if (geo.index !== null) geo = geo.toNonIndexed();

  const pos = geo.getAttribute('position');
  const vertCount = pos.count;
  const day = new Float32Array(vertCount * 3);
  const night = new Float32Array(vertCount * 3);

  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  const n = new THREE.Vector3();

  onProgress?.(0.45, 'bake (fake)');

  // Stand-in for `bakeVertexLight` (T4): per-face albedo jitter, Lambert against
  // the fixed sun for the day channel, hemisphere-only for the night channel.
  for (let f = 0; f < vertCount; f += 3) {
    a.fromBufferAttribute(pos, f + 0);
    b.fromBufferAttribute(pos, f + 1);
    c.fromBufferAttribute(pos, f + 2);
    ab.subVectors(b, a);
    ac.subVectors(c, a);
    n.crossVectors(ab, ac).normalize();

    // Albedo: teal-to-amber wash with ±6% per-face tint jitter (plan default).
    const t = clamp((n.y + 1) * 0.5, 0, 1);
    const jitter = 1 + range(rng, -0.06, 0.06);
    const albedo = new THREE.Color(0.18, 0.52, 0.55).lerp(new THREE.Color(0.92, 0.62, 0.24), t);
    albedo.multiplyScalar(jitter);

    const ndl = Math.max(n.dot(SUN), 0);
    const hemiT = (n.y + 1) * 0.5;

    const hemi = DAY_SKY.clone().lerp(DAY_GROUND, 1 - hemiT);
    const dR = albedo.r * (ndl * 1.35 + hemi.r * 0.55 + 0.05);
    const dG = albedo.g * (ndl * 1.35 + hemi.g * 0.55 + 0.05);
    const dB = albedo.b * (ndl * 1.35 + hemi.b * 0.55 + 0.05);

    const nightHemi = NIGHT_HEMI.clone().multiplyScalar(0.16 + hemiT * 0.1);
    // One face is a deliberate "emitter": >1 values are what feed the bloom.
    const emissive = f === 0 ? 2.4 : 0;
    const nR = albedo.r * (nightHemi.r + 0.02) + emissive * 0.9;
    const nG = albedo.g * (nightHemi.g + 0.02) + emissive * 0.55;
    const nB = albedo.b * (nightHemi.b + 0.02) + emissive * 0.25;

    for (let v = 0; v < 3; v++) {
      const i = (f + v) * 3;
      day[i] = dR;
      day[i + 1] = dG;
      day[i + 2] = dB;
      night[i] = nR;
      night[i + 1] = nG;
      night[i + 2] = nB;
    }
  }

  // THE convention: Float32, itemSize 3, `color` = day, `color1` = night.
  geo.setAttribute('color', new THREE.BufferAttribute(day, 3));
  geo.setAttribute('color1', new THREE.BufferAttribute(night, 3));

  onProgress?.(0.75, 'materials');

  const mat = registry.material('test', () => {
    // vertexColors must stay FALSE: r185's setupDiffuseColor() multiplies
    // vertexColor() into colorNode when the flag is on, which would square the
    // day channel on top of the explicit blend below.
    const m = new THREE.MeshBasicNodeMaterial({ vertexColors: false });
    m.colorNode = mix(vertexColor(0).rgb, vertexColor(1).rgb, uNight);
    return m;
  });

  const root = new THREE.Group();
  root.name = 'testLevel';
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'icosahedron';
  mesh.position.y = 0;
  root.add(mesh);

  onProgress?.(1, 'done');

  let disposed = false;
  let elapsed = 0;

  /** @type {import('../types.js').Level} */
  const level = {
    root,
    colliders: [{ kind: 'circle', x: 0, z: 0, r: 2.4, tag: 'prop' }],
    decals: null,
    probes: null,
    heightAt: () => 0,
    spawnPoints: { player: { x: 0, y: 0, z: 6, yaw: 0, tag: 'player' }, enemies: [] },
    bounds: { minX: -20, minZ: -20, maxX: 20, maxZ: 20, minY: -3, maxY: 3 },
    meta: {
      id: 'test',
      name: 'Stack test',
      seed: ctx.seed ?? 'test',
      size: 40,
      tris: vertCount / 3,
      drawCalls: 1,
      verts: vertCount,
      buildMs: (typeof performance !== 'undefined' ? performance : Date).now() - t0,
      bakeMs: 0,
      sunDir: { x: SUN.x, y: SUN.y, z: SUN.z },
    },
    update(dt) {
      elapsed += dt;
      mesh.rotation.y += dt * 0.6;
      mesh.rotation.x += dt * 0.23;
      // Slow day -> night breathe: proves the shared uniform reaches the GPU.
      uNight.value = (1 - Math.cos((elapsed / 9) * Math.PI * 2)) * 0.5 * 0.9;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      root.removeFromParent();
      geo.dispose();
      // The material belongs to the registry — Engine disposes that for us.
      uNight.value = 0;
    },
  };

  return level;
}

/**
 * Builds the T0 post stage: MSAA via pass samples (NOT renderer.antialias) plus
 * a mild bloom so >1 baked values glow. T2 replaces this with `render/post.js`.
 *
 * @param {import('../core/Engine.js').Engine} engine
 * @returns {import('../types.js').PostStage}
 */
export function createTestPost(engine) {
  // r183 renamed PostProcessing -> RenderPipeline; keep the guard so a version
  // bump either way does not brick the app.
  const RenderPipeline = THREE.RenderPipeline ?? THREE.PostProcessing;
  const pipeline = new RenderPipeline(engine.renderer);

  const scenePass = pass(engine.scene, engine.camera, { samples: 4 });
  const color = scenePass.getTextureNode('output');
  // strength, radius, threshold — mild: only the emissive face should smear.
  const bloomPass = bloom(color, 0.32, 0.55, 0.85);
  pipeline.outputNode = color.add(bloomPass);

  return pipeline;
}
